'use client';

import type { MouseEventHandler, ReactElement } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { DataElScope } from '@/utils/dataEl';
import {
  NAVBAR_LOADING_KEY,
  NAVBAR_LOGGED_KEY,
  NAVBAR_LOGGED_LANDING_KEY,
  NAVBAR_NON_LOGGED_KEY,
  NAVBAR_NON_LOGGED_LANDING_KEY,
} from './Cpagelayout.constants';
import { buildNavbarStyles } from './Cpagelayout.styles';
import { LoadingNavbar } from './LoadingNavbar';
import { LoggedNavbar } from './LoggedNavbar';
import { NonLoggedNavbarShell } from './NonLoggedNavbarShell';

interface CpagelayoutNavbarProps {
  dataElScope: DataElScope;
  authError: boolean;
  authLoading: boolean;
  isAnyModalOpen: boolean;
  isCenterModalOpen: boolean;
  isLandingPage: boolean;
  isMobile: boolean;
  isScrolling: boolean;
  isSessionsModalOpen: boolean;
  isUserMenuModalOpen: boolean;
  onHamburgerClick: () => void;
  onLoggedLogoClick: MouseEventHandler<HTMLAnchorElement>;
  onModalClose: () => void;
  onNonLoggedLogoClick: () => void;
  onProfileClick: () => void;
  profileImageSrc?: string;
  showNonLoggedNavbar: boolean;
}

interface NavbarKeyInput {
  isLandingPage: boolean;
}

function getNonLoggedNavbarKey({
  isLandingPage,
}: NavbarKeyInput): string {
  return isLandingPage ? NAVBAR_NON_LOGGED_LANDING_KEY : NAVBAR_NON_LOGGED_KEY;
}

function getLoggedNavbarKey({
  isLandingPage,
}: NavbarKeyInput): string {
  return isLandingPage ? NAVBAR_LOGGED_LANDING_KEY : NAVBAR_LOGGED_KEY;
}

export function CpagelayoutNavbar({
  dataElScope,
  authError,
  authLoading,
  isAnyModalOpen,
  isCenterModalOpen,
  isLandingPage,
  isMobile,
  isScrolling,
  isSessionsModalOpen,
  isUserMenuModalOpen,
  onHamburgerClick,
  onLoggedLogoClick,
  onModalClose,
  onNonLoggedLogoClick,
  onProfileClick,
  profileImageSrc,
  showNonLoggedNavbar,
}: CpagelayoutNavbarProps): ReactElement {
  const navbarStyles = buildNavbarStyles({ isCenterModalOpen });

  if (authLoading) {
    return (
        <AnimatePresence mode="wait">
          <LoadingNavbar
            dataElScope={dataElScope}
            key={NAVBAR_LOADING_KEY}
            isLandingPage={isLandingPage}
            isMobile={isMobile}
            navbarStyles={navbarStyles}
            onLogoClick={onLoggedLogoClick}
          />
        </AnimatePresence>
    );
  }

  if (showNonLoggedNavbar) {
    return (
        <AnimatePresence mode="wait">
          <NonLoggedNavbarShell
            dataElScope={dataElScope}
            key={getNonLoggedNavbarKey({ isLandingPage })}
            isCenterModalOpen={isCenterModalOpen}
            isLandingPage={isLandingPage}
            isScrolling={isScrolling}
            onLogoClick={onNonLoggedLogoClick}
          />
        </AnimatePresence>
    );
  }

  return (
      <AnimatePresence mode="wait">
        <LoggedNavbar
          dataElScope={dataElScope}
          key={getLoggedNavbarKey({ isLandingPage })}
          authError={authError}
          isAnyModalOpen={isAnyModalOpen}
          isCenterModalOpen={isCenterModalOpen}
          isLandingPage={isLandingPage}
          isMobile={isMobile}
          isScrolling={isScrolling}
          isSessionsModalOpen={isSessionsModalOpen}
          isUserMenuModalOpen={isUserMenuModalOpen}
          navbarStyles={navbarStyles}
          onHamburgerClick={onHamburgerClick}
          onLogoClick={onLoggedLogoClick}
          onModalClose={onModalClose}
          onProfileClick={onProfileClick}
          profileImageSrc={profileImageSrc}
        />
      </AnimatePresence>
  );
}
